if (!customElements.get('sv-oddit-store-slider')) {
  customElements.define(
    'sv-oddit-store-slider',
    class extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-sv-store-viewport]');
        this.progressBars = [...this.querySelectorAll('[data-sv-store-progress]')];
        this.isReviewSlider = this.classList.contains('sv-oddit-store-slider--reviews');
        const section = this.closest('section');
        this.previousButtons = section ? [...section.querySelectorAll('[data-sv-store-previous]')] : [];
        this.nextButtons = section ? [...section.querySelectorAll('[data-sv-store-next]')] : [];
        if (!this.viewport) return;

        this.viewport.classList.add('is-initializing');
        this.sortProductCards();
        this.viewport.scrollTo({ left: 0, behavior: 'auto' });
        this.dragged = false;
        this.onScroll = this.update.bind(this);
        this.onWheel = this.handleWheel.bind(this);
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);
        this.onClickCapture = this.handleClickCapture.bind(this);
        this.onScrollEnd = this.finishSettling.bind(this);
        this.previousButtons.forEach((button) => button.addEventListener('click', () => this.move(-1)));
        this.nextButtons.forEach((button) => button.addEventListener('click', () => this.move(1)));
        this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
        this.viewport.addEventListener('scrollend', this.onScrollEnd);
        this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('click', this.onClickCapture, true);
        this.viewport.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            this.move(event.key === 'ArrowLeft' ? -1 : 1);
          }
        });
        this.resizeObserver = new ResizeObserver(this.onScroll);
        this.resizeObserver.observe(this.viewport);
        requestAnimationFrame(() => {
          this.viewport.scrollTo({ left: 0, behavior: 'auto' });
          this.viewport.classList.remove('is-initializing');
          this.onScroll();
        });
      }

      sortProductCards() {
        const track = this.viewport.querySelector('.sv-oddit-store-slider__track');
        if (!track) return;
        const cards = [...track.querySelectorAll('[data-sv-review-count]')];
        cards
          .map((card, index) => ({
            card,
            index,
            soldOut: card.dataset.svAvailable === 'false' ? 1 : 0,
            reviewCount: Number.parseInt(card.dataset.svReviewCount, 10) || 0,
          }))
          .sort((a, b) => a.soldOut - b.soldOut || b.reviewCount - a.reviewCount || a.index - b.index)
          .forEach(({ card }) => track.append(card));
      }

      disconnectedCallback() {
        this.viewport?.removeEventListener('scroll', this.onScroll);
        this.viewport?.removeEventListener('scrollend', this.onScrollEnd);
        this.viewport?.removeEventListener('wheel', this.onWheel);
        this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport?.removeEventListener('click', this.onClickCapture, true);
        this.removePointerListeners();
        clearTimeout(this.wheelSettleTimer);
        clearTimeout(this.settleFallbackTimer);
        this.resizeObserver?.disconnect();
      }

      getSnapPoints() {
        const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
        const viewportRect = this.viewport.getBoundingClientRect();
        const points = [...this.viewport.querySelectorAll('[data-sv-slider-item]')].map((item) => {
          const itemPosition = item.getBoundingClientRect().left - viewportRect.left + this.viewport.scrollLeft;
          return Math.min(maximum, Math.max(0, itemPosition));
        });
        return [...new Set(points.map((point) => Math.round(point)))].sort((a, b) => a - b);
      }

      getNearestSnapPoint(position = this.viewport.scrollLeft) {
        const points = this.getSnapPoints();
        if (!points.length) return 0;
        return points.reduce((nearest, point) => (
          Math.abs(point - position) < Math.abs(nearest - position) ? point : nearest
        ), points[0]);
      }

      move(direction) {
        const points = this.getSnapPoints();
        if (!points.length) return;
        const position = this.viewport.scrollLeft;
        const tolerance = 3;
        let target;
        if (direction > 0) {
          target = points.find((point) => point > position + tolerance);
          target = target ?? points.at(-1);
        } else {
          target = [...points].reverse().find((point) => point < position - tolerance);
          target = target ?? points[0];
        }
        this.settleTo(target);
      }

      settleTo(target) {
        clearTimeout(this.wheelSettleTimer);
        clearTimeout(this.settleFallbackTimer);
        this.settleTarget = target;
        this.viewport.classList.remove('is-wheeling');
        this.viewport.classList.add('is-settling');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.viewport.scrollTo({ left: target, behavior: reducedMotion ? 'auto' : 'smooth' });
        if (reducedMotion) {
          this.finishSettling();
          return;
        }
        this.settleFallbackTimer = setTimeout(() => this.finishSettling(), 520);
      }

      finishSettling() {
        if (!this.viewport?.classList.contains('is-settling')) return;
        clearTimeout(this.settleFallbackTimer);
        this.viewport.scrollTo({ left: this.settleTarget, behavior: 'auto' });
        requestAnimationFrame(() => this.viewport.classList.remove('is-settling'));
      }

      handleWheel(event) {
        const maximum = this.viewport.scrollWidth - this.viewport.clientWidth;
        if (maximum <= 1) return;
        const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        if (!delta) return;
        const atStart = this.viewport.scrollLeft <= 1;
        const atEnd = this.viewport.scrollLeft >= maximum - 1;
        if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;
        event.preventDefault();
        clearTimeout(this.wheelSettleTimer);
        this.viewport.classList.remove('is-settling');
        this.viewport.classList.add('is-wheeling');
        this.viewport.scrollBy({ left: delta, behavior: 'auto' });
        if (this.isReviewSlider) {
          this.wheelSettleTimer = setTimeout(() => {
            this.viewport.classList.remove('is-wheeling');
          }, 220);
          return;
        }
        this.wheelSettleTimer = setTimeout(() => {
          this.settleTo(this.getNearestSnapPoint());
        }, 140);
      }

      handlePointerDown(event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('button, input, select, textarea, [role="button"]')) return;
        this.pointerId = event.pointerId;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.dragStartScroll = this.viewport.scrollLeft;
        this.dragged = false;
        clearTimeout(this.wheelSettleTimer);
        clearTimeout(this.settleFallbackTimer);
        this.viewport.classList.remove('is-settling', 'is-wheeling');
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerUp);
      }

      handlePointerMove(event) {
        if (event.pointerId !== this.pointerId) return;
        const distanceX = event.clientX - this.dragStartX;
        const distanceY = event.clientY - this.dragStartY;
        if (!this.dragged) {
          if (Math.abs(distanceX) <= 8 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
          this.dragged = true;
          this.viewport.classList.add('is-dragging');
          this.viewport.setPointerCapture?.(event.pointerId);
        }
        this.viewport.scrollLeft = this.dragStartScroll - distanceX;
      }

      handlePointerUp(event) {
        if (event.pointerId !== this.pointerId) return;
        if (this.viewport.hasPointerCapture?.(event.pointerId)) {
          this.viewport.releasePointerCapture(event.pointerId);
        }
        this.removePointerListeners();
        if (this.dragged) {
          if (this.isReviewSlider) {
            this.viewport.classList.remove('is-dragging');
            return;
          }
          const target = this.getNearestSnapPoint();
          this.viewport.classList.add('is-settling');
          this.viewport.classList.remove('is-dragging');
          this.settleTo(target);
        } else {
          this.viewport.classList.remove('is-dragging');
        }
      }

      removePointerListeners() {
        this.viewport?.removeEventListener('pointermove', this.onPointerMove);
        this.viewport?.removeEventListener('pointerup', this.onPointerUp);
        this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
        this.pointerId = null;
      }

      handleClickCapture(event) {
        if (!this.dragged) return;
        event.preventDefault();
        event.stopPropagation();
        this.dragged = false;
      }

      update() {
        const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
        const position = Math.max(0, this.viewport.scrollLeft);
        const progress = maximum > 0 ? Math.min(1, position / maximum) : 0;
        const visibleRatio = Math.min(1, this.viewport.clientWidth / Math.max(this.viewport.scrollWidth, 1));
        const thumbWidth = Math.max(18, visibleRatio * 100);
        const thumbLeft = progress * (100 - thumbWidth);
        this.progressBars.forEach((bar) => {
          bar.style.width = `${thumbWidth}%`;
          bar.style.left = `${thumbLeft}%`;
        });
        this.previousButtons.forEach((button) => { button.disabled = maximum === 0 || position <= 2; });
        this.nextButtons.forEach((button) => { button.disabled = maximum === 0 || position >= maximum - 2; });
      }
    },
  );
}

if (!customElements.get('sv-oddit-store-card-form')) {
  customElements.define(
    'sv-oddit-store-card-form',
    class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.button = this.querySelector('[type="submit"]');
        if (!this.form || !this.button) return;
        this.onSubmit = this.handleSubmit.bind(this);
        this.form.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
      }

      async handleSubmit(event) {
        event.preventDefault();
        if (this.button.disabled || this.button.classList.contains('is-loading')) return;

        this.button.classList.add('is-loading');
        this.button.setAttribute('aria-disabled', 'true');
        const cartNotification = document.querySelector('cart-notification');
        const shopRoot = window.Shopify?.routes?.root || '/';
        const cartAddUrl = `${shopRoot}cart/add.js`;

        let productWasAdded = false;
        try {
          const response = await fetch(cartAddUrl, {
            method: 'POST',
            body: new FormData(this.form),
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
          });
          const result = await response.json();
          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Unable to add this product to the cart.');
          }
          productWasAdded = true;

          if (cartNotification?.refreshFromServer) {
            await cartNotification.refreshFromServer();
          } else {
            window.location.assign(window.routes?.cart_url || '/cart');
          }
        } catch (error) {
          console.error('[Swiftvein store] Add to cart failed:', error);
          if (productWasAdded) {
            window.location.assign(window.routes?.cart_url || '/cart');
          } else {
            this.form.submit();
          }
          return;
        } finally {
          this.button.classList.remove('is-loading');
          this.button.removeAttribute('aria-disabled');
        }
      }
    },
  );
}

if (!customElements.get('sv-oddit-storefront')) {
  customElements.define(
    'sv-oddit-storefront',
    class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (event) => {
          const modal = this.querySelector('[data-sv-store-refurb-modal]');
          const openButton = event.target.closest('[data-sv-store-refurb-open]');
          const closeButton = event.target.closest('[data-sv-store-refurb-close]');
          if (openButton && modal) {
            modal.showModal();
            return;
          }
          if (closeButton && modal) {
            modal.close();
            return;
          }
          if (event.target === modal) {
            modal.close();
            return;
          }

          const link = event.target.closest('.sv-oddit-store__category-nav a[href^="#"]');
          if (!link || !this.contains(link)) return;
          const target = this.querySelector(link.getAttribute('href'));
          if (!target) return;
          event.preventDefault();
          target.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
          });
          history.replaceState(null, '', link.getAttribute('href'));
        });
      }
    },
  );
}

// Reveal the final product fully when the mobile navigator reaches its end.
if (!customElements.get('sv-oddit-store-nav')) {
  customElements.define('sv-oddit-store-nav', class extends HTMLElement {
    connectedCallback() {
      this.updateOverflow = () => {
        this.dataset.svOverflowRight = String(this.scrollWidth - this.clientWidth - this.scrollLeft > 2);
      };
      this.addEventListener('scroll', this.updateOverflow, { passive: true });
      this.resizeObserver = new ResizeObserver(this.updateOverflow);
      this.resizeObserver.observe(this);
      const track = this.querySelector('.sv-oddit-store__product-nav-track');
      if (track) this.resizeObserver.observe(track);
      this.updateOverflow();
    }
    disconnectedCallback() {
      this.removeEventListener('scroll', this.updateOverflow);
      this.resizeObserver?.disconnect();
    }
  });
}
